// Command registry: screens register handlers for command ids while mounted. Keyboard bindings and
// the palette both dispatch through here, so a command is available exactly when a handler exists.
import { useEffect, useRef, useSyncExternalStore } from "react";
import type { CommandId } from "./keymap";

type Handler = () => void;

const stacks = new Map<CommandId, { token: symbol; run: () => void }[]>();
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version++;
  for (const listener of listeners) listener();
}

function register(id: CommandId, run: () => void): () => void {
  const token = Symbol(id);
  const stack = stacks.get(id) ?? [];
  stack.push({ token, run });
  stacks.set(id, stack);
  emit();
  return () => {
    const current = stacks.get(id);
    if (!current) return;
    const next = current.filter((entry) => entry.token !== token);
    if (next.length) stacks.set(id, next);
    else stacks.delete(id);
    emit();
  };
}

/** Run the most recently registered handler for `id`. Returns false when nothing handles it. */
export function runCommand(id: CommandId): boolean {
  const stack = stacks.get(id);
  const top = stack?.[stack.length - 1];
  if (!top) return false;
  top.run();
  return true;
}

export function hasCommand(id: CommandId): boolean {
  return (stacks.get(id)?.length ?? 0) > 0;
}

/** Register `handler` for `id` while the component is mounted and `enabled` is true. */
export function useCommand(id: CommandId, handler: Handler, enabled = true): void {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!enabled) return;
    return register(id, () => ref.current());
  }, [id, enabled]);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the set of available commands changes. */
export function useCommandsVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
}
